import React, { useEffect, useState } from "react";
import { ShieldAlert, AlertTriangle, ExternalLink } from "lucide-react";
import Logo from "@/components/Logo";
import { safeStorage } from "@/lib/safeStorage";

const STORAGE_KEY = "bv_age_verified_v1";

export default function AgeGate() {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const v = safeStorage.getItem(STORAGE_KEY);
    if (!v) setOpen(true);
  }, []);

  const accept = () => {
    safeStorage.setItem(STORAGE_KEY, new Date().toISOString());
    setOpen(false);
  };

  const decline = () => {
    // Redirect to a safe site
    window.location.href = "https://www.google.com";
  };

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="age-gate-title"
      data-testid="age-gate-modal"
    >
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/90 backdrop-blur-md" />

      {/* Card */}
      <div className="relative w-full max-w-lg glass-strong border border-gold/30 rounded-2xl shadow-2xl overflow-hidden animate-fade-up">
        {/* Top stripe */}
        <div className="h-1.5 bg-gradient-to-r from-amber-500 via-gold to-amber-700" />

        <div className="p-7 md:p-9">
          <div className="flex items-center gap-3 mb-5">
            <Logo size={52} />
            <div>
              <div className="font-display font-black text-xl text-white leading-none">
                Bonusz<span className="text-gold">vadász</span>
              </div>
              <div className="text-[10px] uppercase tracking-widest text-white/40 mt-1">
                Független szakértői portál
              </div>
            </div>
          </div>

          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-rose-500/15 border border-rose-500/30 mb-4">
            <ShieldAlert size={14} className="text-rose-300" />
            <span className="text-xs font-semibold uppercase tracking-wider text-rose-200">Életkor-ellenőrzés</span>
          </div>

          <h2
            id="age-gate-title"
            className="font-display font-black text-3xl md:text-4xl text-white leading-tight"
          >
            Elmúltál már <span className="text-gold">18 éves</span>?
          </h2>

          <p className="text-white/70 text-sm md:text-base mt-4 leading-relaxed">
            Az oldal szerencsejátékkal kapcsolatos információkat, értékeléseket és bónusz-ajánlatokat tartalmaz.
            A magyar jogszabályok értelmében ezeket a tartalmakat kizárólag <strong className="text-white">betöltött 18.
            életévét meghaladott</strong> személyek tekinthetik meg.
          </p>

          <div className="mt-5 p-3.5 rounded-lg bg-amber-500/10 border border-amber-500/25 flex gap-2.5 items-start">
            <AlertTriangle size={16} className="text-amber-300 mt-0.5 shrink-0" />
            <p className="text-xs text-amber-100/90 leading-relaxed">
              A szerencsejáték függőséget okozhat. Mindig felelősen játssz, csak annyit tegyél fel, amennyit megengedhetsz
              magadnak elveszíteni. Segítségre van szükséged? Látogass el a{" "}
              <a href="/felelos-jatek" className="underline hover:text-amber-200">Felelős játék</a> oldalunkra.
            </p>
          </div>

          <div className="mt-7 flex flex-col sm:flex-row gap-3">
            <button
              onClick={accept}
              className="flex-1 gold-btn rounded-lg px-5 py-3.5 text-base font-bold shadow-lg shadow-gold/20"
              data-testid="age-gate-accept"
            >
              Igen, elmúltam 18 — Belépés
            </button>
            <button
              onClick={decline}
              className="flex-1 rounded-lg px-5 py-3.5 text-sm font-medium border border-white/15 bg-white/5 text-white/80 hover:bg-white/10 hover:text-white transition-colors flex items-center justify-center gap-2"
              data-testid="age-gate-decline"
            >
              Nem, kilépek <ExternalLink size={14} />
            </button>
          </div>

          <div className="mt-5 text-[11px] text-white/40 text-center">
            A belépéssel elfogadod az <a href="/aszf" className="underline hover:text-white/60">ÁSZF</a>-et és az{" "}
            <a href="/adatvedelem" className="underline hover:text-white/60">Adatvédelmi tájékoztatót</a>.
          </div>
        </div>
      </div>
    </div>
  );
}
